import AutoAwesome from '@mui/icons-material/AutoAwesome';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useContext, useEffect, useRef, useState } from 'react';

import { useAnalytics } from '~/analytics';
import { UserContext } from '~/auth/userContext';
import DialogTitleWithCloseButton from '~/components/DialogTitleWithCloseButton';
import Feedback from '~/components/feedback/Feedback';
import {
  getIdentifiedSavings,
  ReviewContent,
  selectFindings,
  STATUS_BG,
  STATUS_CONFIG,
} from '~/components/invoices/AiReviewTab';
import { useRefreshInsights } from '~/data/hooks/useInvoice';
import type { AiReviewStatus, Invoice } from '~/data/schemas/invoice';
import { isInvoiceFinal } from '~/data/schemas/invoice';
import { BUTTON_SX, CARD_SX } from '~/theme';
import { formatCurrency } from '~/utils/format';

/**
 * The AI review in the page header, as one compact card beside the invoice title: what
 * the contract analysis suggests, how sure it is, what following it would save, and
 * why. The findings are the working behind it, so they sit one click away.
 */
export function AiReviewSummary({
  invoice,
  autoGenerate = true,
}: {
  invoice: Invoice | undefined;
  /** Off in the preview, so passing over a row never starts a review. */
  autoGenerate?: boolean;
}) {
  const { currencyCode } = useContext(UserContext);
  const { track } = useAnalytics();
  const invoiceId = invoice?.id ?? null;
  const insights = invoice?.insights ?? null;
  const finalized = invoice ? isInvoiceFinal(invoice) : false;
  const { refreshInsights, loading, error } = useRefreshInsights(invoiceId);
  const [findingsOpen, setFindingsOpen] = useState(false);

  // Same auto-generation the AI Review tab did on mount, moved here because this is
  // what's always mounted now. The per-invoice latch is load-bearing: when
  // `refreshInsights` fails (e.g. no active contract) `error` goes truthy and `loading`
  // returns to false while `insights.status` stays null, so without it the effect
  // re-fires in a tight loop against the server.
  const lastAttemptedInvoiceIdRef = useRef<string | null>(null);
  // Generation can also answer 200 without producing a status: `loading` false, `error`
  // null, `insights.status` still null — indistinguishable from "hasn't started".
  // Recording the settle lets the card say nothing instead of holding a skeleton
  // nothing will replace. Set from the promise rather than the effect body, which would
  // be a cascading render.
  const [settledInvoiceId, setSettledInvoiceId] = useState<string | null>(null);
  useEffect(() => {
    if (!invoiceId || !autoGenerate) return;
    if (finalized) return;
    if (insights?.status != null) return;
    if (lastAttemptedInvoiceIdRef.current === invoiceId) return;
    lastAttemptedInvoiceIdRef.current = invoiceId;
    track('invoice_ai_review_initiated', { invoice_id: invoiceId, trigger: 'auto' });
    void refreshInsights().finally(() => setSettledInvoiceId(invoiceId));
  }, [invoiceId, autoGenerate, finalized, insights?.status, refreshInsights, track]);

  const willAutoFire =
    autoGenerate &&
    !!invoiceId &&
    !finalized &&
    !insights?.status &&
    !error &&
    settledInvoiceId !== invoiceId;

  if (loading || willAutoFire) {
    return (
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        width="100%"
        data-testid="ai-review-skeleton"
      >
        <AutoAwesome fontSize="small" color="primary" />
        <Box flex={1}>
          <Skeleton width="40%" />
          <Skeleton width="80%" />
        </Box>
      </Stack>
    );
  }

  // No review and none coming (finalized, or generation failed). Say nothing rather
  // than put an error banner in the page header.
  if (!insights?.status) {
    return (error || !autoGenerate) && !finalized && invoiceId ? (
      <Button
        size="small"
        startIcon={<AutoAwesome />}
        onClick={() => {
          track('invoice_ai_review_initiated', {
            invoice_id: invoiceId,
            trigger: error ? 'retry' : 'manual',
          });
          void refreshInsights();
        }}
        sx={BUTTON_SX}
      >
        {error ? 'Retry AI review' : 'Run AI review'}
      </Button>
    ) : null;
  }

  const status = insights.status as AiReviewStatus;
  const statusConfig = STATUS_CONFIG[status];
  const themeColor = statusConfig?.color ?? 'info';
  const { bg } = STATUS_BG[themeColor];
  const accent = themeColor !== 'default' ? `${themeColor}.main` : 'text.secondary';

  const invoiceAmount = invoice?.proposedAmount ?? null;
  const findings = selectFindings(insights);
  const missingEvidence = insights.missingEvidence ?? [];
  const hasDetail = findings.length > 0 || missingEvidence.length > 0;
  const savings = getIdentifiedSavings(insights, invoiceAmount);

  return (
    <>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          p: 1.5,
          // Same shape as CARD_SX, with a heavier border tinted to the verdict.
          ...CARD_SX,
          border: '2px solid',
          borderColor: themeColor !== 'default' ? `${themeColor}.light` : 'divider',
          background: bg,
        }}
      >
        <Tooltip title="Regenerate review">
          <span style={{ position: 'absolute', top: 4, right: 4 }}>
            <IconButton
              size="small"
              onClick={() => {
                if (invoiceId) {
                  track('invoice_ai_review_initiated', {
                    invoice_id: invoiceId,
                    trigger: 'manual',
                  });
                }
                void refreshInsights();
              }}
              disabled={loading}
              aria-label="Regenerate review"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Box minWidth={0} pr={4}>
          <Stack direction="row" alignItems="baseline" spacing={1} flexWrap="wrap" useFlexGap>
            <AutoAwesome fontSize="small" sx={{ color: accent, alignSelf: 'center' }} />
            <Typography variant="caption" color="text.secondary" letterSpacing={0.5} noWrap>
              CONTRACT ANALYSIS (AI):
            </Typography>
            {statusConfig ? (
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: accent }}>
                {statusConfig.action}
              </Typography>
            ) : null}
            {savings != null && savings > 0 ? (
              <Stack direction="row" alignItems="baseline" spacing={0.75} whiteSpace="nowrap">
                <Typography variant="h6" fontWeight={700} sx={{ color: 'success.dark' }}>
                  {formatCurrency({ value: savings, currencyCode })}
                </Typography>
                <Typography variant="body2" sx={{ color: 'success.dark' }}>
                  - reduction
                </Typography>
              </Stack>
            ) : null}
          </Stack>

          {insights.primaryInsight || hasDetail ? (
            <Box mt={0.75}>
              {insights.primaryInsight ? (
                <Typography variant="body2" color="text.primary" component="span">
                  {insights.primaryInsight}
                </Typography>
              ) : null}
              {hasDetail ? (
                <Button
                  size="small"
                  onClick={() => setFindingsOpen(true)}
                  sx={{ ...BUTTON_SX, ml: 0.5, py: 0, minWidth: 0, verticalAlign: 'baseline' }}
                >
                  {findings.length > 0 ? `View findings (${findings.length})` : 'View details'}
                </Button>
              ) : null}
            </Box>
          ) : null}
        </Box>
      </Box>

      <Dialog
        open={findingsOpen}
        onClose={() => setFindingsOpen(false)}
        fullWidth
        maxWidth="md"
        disableScrollLock
      >
        <DialogTitleWithCloseButton
          onClose={() => setFindingsOpen(false)}
          title={invoice ? `CONTRACT ANALYSIS (AI) · #${invoice.number}` : 'CONTRACT ANALYSIS (AI)'}
        />
        <DialogContent dividers>
          <Box mb={2.5}>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
              <AutoAwesome fontSize="small" sx={{ color: accent }} />
              {statusConfig ? (
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: accent }}>
                  {statusConfig.action}
                </Typography>
              ) : null}
              {insights.confidence != null ? (
                <Typography variant="caption" color="text.secondary">
                  {Math.round(insights.confidence * 100)}% confident
                </Typography>
              ) : null}
            </Stack>

            {insights.primaryInsight ? (
              <Typography variant="body2" mt={0.75}>
                {insights.primaryInsight}
              </Typography>
            ) : null}
          </Box>

          <ReviewContent
            insights={insights}
            invoiceAmount={invoiceAmount}
            currencyCode={currencyCode}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Feedback
            feedbackKey="ai-review"
            runId={insights.runId}
            placeholder="Was this review accurate?"
            onSubmitted={({ isPositive }) => {
              if (!invoiceId) return;
              track('invoice_ai_review_feedback', {
                invoice_id: invoiceId,
                helpful: isPositive,
              });
            }}
          />
        </DialogActions>
      </Dialog>
    </>
  );
}
